import React, { useContext, useEffect, useState } from "react";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import Table from "./Table";
import Pagination from "../Pagination";
import UserView from "./UserView";
import AuthContext from "context/AuthContext";
import dayjs from "dayjs";
import ODateRangePicker from "components/shared/datePicker/ODateRangePicker";
import { useTranslation } from "react-i18next";
import CompanyPassword from "./CompanyPassword";
import helpers from "../../utils/helpers";
import OSearch from "components/reusable/OSearch";

function CompanyManager() {
  const { t } = useTranslation();
  const { logoutUser, user, updatePageName } = useContext(AuthContext);
  const manager = user?.permission?.find((e) => e.manager === "company_manager") ?? {};
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });
  const [viewShowModal, setViewShowModal] = useState(false);
  const [passwordShowModal, setPasswordShowModal] = useState(false);
  const [allCompany, setAllCompany] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [item, setItem] = useState("");
  const [isDelete, setIsDelete] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [filterData, setFilterData] = useState({
    category: "",
    searchkey: "",
    categoryCountry: "",
    categoryState: "",
    startDate: "",
    endDate: "",
    isReset: false,
    isFilter: false,
  });
  const [sort, setSort] = useState({
    sortBy: "createdAt",
    sortType: "desc",
  });

  const [countryList, setCountryList] = useState("");
  const [cityList, setCityList] = useState("");
  const [stateList, setStateList] = useState([])

  const handleApiError = (error) => {
    console.error("error ", error);
    setPaginationObj({});
    if (error.response?.status === 401 || error.response?.status === 409) {
      logoutUser();
    }
  };

  useEffect(() => {
    updatePageName(t("COMPANY_MANAGER"));
  }, []);

  const getCountryList = async () => {
    try {
      const res = await apiGet(apiPath.getCountryList, {});
      setCountryList(res?.data?.results?.allCountries);
      setCityList(res?.data?.results?.allCities);
    } catch (error) {
      console.log("error:", error);
    }
  };
  const getStateListByCountry = async (countryId)=>{
     try {
      const res = await apiGet(`${apiPath.getStateListByCountry}/${countryId}`, {});
      setStateList(res?.data?.results?.allStates);
    } catch (error) {
      console.log("error:", error);
    }
  }

  const getCompany = async (data) => {
    try {
      const { isFilter, category, startDate, endDate, searchkey, categoryCountry, categoryState } = filterData;
      if ((data?.deletePage && allCompany?.length >= 1) || (isFilter && category && data?.statusChange && allCompany?.length >= 1)) {
        if (allCompany?.length >= 1) {
          setPage(page - 1);
          setIsDelete(true);
          setPaginationObj({ ...paginationObj, page: page - 1 });
        }
      } else {
        setIsDelete(false);
      }
      const payload = {
        isSubscribedStatus: category,
        page,
        pageSize,
        endDate: helpers?.ternaryCondition(endDate, dayjs(endDate).format("YYYY-MM-DD"), null),
        startDate: helpers?.ternaryCondition(startDate, dayjs(startDate).format("YYYY-MM-DD"), null),
        keyword: searchkey,
        sortBy: sort.sortBy,
        sortType: sort.sortType,
        countryId: categoryCountry,
        stateId: categoryState,
      };

      const path = apiPath.getCompany;
      const result = await apiGet(path, payload);
      if (result?.status === 200) {
        const response = result?.data?.results;
        const resultStatus = result?.data?.success;
        setAllCompany(response?.docs);
        setPaginationObj({
          ...paginationObj,
          page: helpers?.ternaryCondition(resultStatus, response.page, null),
          pageCount: helpers?.ternaryCondition(resultStatus, response.totalPages, null),
          perPageItem: helpers?.ternaryCondition(resultStatus, response?.docs?.length, null),
          totalItems: helpers?.ternaryCondition(resultStatus, response.totalDocs, null),
        });
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleUserView = (items) => {
    setItem(items);
    setViewShowModal(true);
  };
  useEffect(() => {
    getCountryList();
  }, []);

  useEffect(() => {
    if(filterData.categoryCountry != "")
      getStateListByCountry(filterData.categoryCountry);
  }, [filterData]);

  const setCountryType = (e) => {
    setFilterData({
      ...filterData,
      categoryCountry: e.target.value,
      categoryState: "",
      isFilter: true,
      isReset: false,
    });
    setPage(1);
    setIsDelete(true);
  };

  const setStateType = (e) => {
    setFilterData({
      ...filterData,
      categoryState: e.target.value,
      isFilter: true,
      isReset: false,
    });
    setPage(1);
    setIsDelete(true);
  };

  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
    } else if (searchTerm || !filterData?.isReset) {
      setFilterData({
        ...filterData,
        isReset: false,
        searchkey: helpers?.ternaryCondition(debouncedSearchTerm, debouncedSearchTerm, ""),
        isFilter: helpers?.ternaryCondition(debouncedSearchTerm, true, false),
      });
      setPage(1);
    }
  }, [debouncedSearchTerm]);

  const handleUserPassword = (items) => {
    setItem(items);
    setPasswordShowModal(true);
  };
  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPage(newPage);
  };
  useEffect(() => {
    getCompany();
  }, [page, filterData, sort]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  const handleDateChange = (start, end) => {
    setPage(1);
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true,
    });
    setIsDelete(true);
  };

  const handleResetItem = () => {
    setFilterData({
      startDate: "",
      endDate: "",
      category: "",
      searchkey: "",
      categoryCountry: "",
      categoryState: "",
      isReset: true,
      isFilter: false,
    });
    setPage(1);
    setIsDelete(true);
    setSearchTerm("");
  };

  return (
    <div>
      <div className="bg-[#F9F9F9]">
        <div className="px-3 py-4">
          <div className="bg-white border border-[#E9EDF9] rounded-lg">
            <form className="border-b border-b-[#E3E3E3] grid 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1 gap-2 px-4 py-3">
              <div className="col-span-3 flex flex-wrap  items-center">
                <div className="flex flex-wrap items-center lg:pt-0 pt-3 mb-2">
                  <div className="relative flex items-center mb-3">
                    <OSearch searchTerm={searchTerm} setSearchTerm={setSearchTerm} placeholder={t("SEARCH_BY_COMPANY_NAME_NUMBER")} />
                  </div>
                  <ODateRangePicker handleDateChange={handleDateChange} isReset={filterData?.isReset} setIsReset={setFilterData} />
                  <div className="flex items-center mb-3 ml-3">
                    <select
                      className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF]  dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer"
                      placeholder=" "
                      type="select"
                      id="countryID"
                      value={filterData?.categoryCountry}
                      onChange={(e) => setCountryType(e)}
                    >
                      <option key={""} value="">{t("SELECT_COUNTRY")}</option>
                      {countryList &&
                        countryList.map((items) => {
                          return <option key={items?._id} value={items?._id}>{items?.name}</option>;
                        })}
                    </select>
                  </div>
                  <div className="flex items-center mb-3 ml-3">
                    <select
                      className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF]  dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer"
                      placeholder=" "
                      type="select"
                      id="stateID"
                      value={filterData?.categoryState}
                      onChange={(e) => setStateType(e)}
                    >
                      <option value="">{t("SELECT_STATE")}</option>
                      {stateList?.length &&
                        stateList?.map(
                          (i, key) =>
                            i?.countryID == filterData?.categoryCountry && (
                              <option key={key} value={i?._id}>
                                {i?.name}
                              </option>
                            )
                        )}
                    </select>
                  </div>
                  <button type="button" onClick={handleResetItem} className="bg-LightBlue text-sm px-8 ml-3 mb-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue sm:w-auto w-1/2">
                    {t("O_RESET")}
                  </button>
                </div>
              </div>
            </form>
            <Table allCompany={allCompany} user={user} getCompany={getCompany} handleUserView={handleUserView} handleUserPassword={handleUserPassword} page={page} setSort={setSort} sort={sort} manager={manager} />
            {helpers.ternaryCondition(paginationObj?.totalItems, <Pagination handlePageClick={handlePageClick} options={paginationObj} isDelete={isDelete} page={page} />, null)}
          </div>
        </div>
      </div>
      <>
        {viewShowModal && <UserView setViewShowModal={setViewShowModal} getCompany={getCompany} item={item} handleUserView={handleUserView} />}
        {passwordShowModal && <CompanyPassword setPasswordShowModal={setPasswordShowModal} getCompany={getCompany} allCompany={allCompany} item={item} />}
      </>
    </div>
  );
}
export default CompanyManager;
